// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import {defineStore, storeToRefs} from 'pinia'
import {sortSizes as sortApparelSizes} from 'apparel-sorter'
import {config} from "@/core/config";
import {searchLogger, searchErrorLogger} from "@/core/Logger";
import {AggregationInterface, FilterRangeInterface} from "@/core/types";
import {useUrlStore} from '@/store/SerpPage/UrlStore'
import {useSerpResultsStore} from '@/store/SerpPage/SerpResultsStore'
import {usePaginationStore} from "@/store/SerpPage/PaginationStore";
import {SmartSearchConfig} from "@/main";
import {useSortingStore} from "@/store/SerpPage/SortingStore";
import {useSearchQueryStore} from "@/store/SearchQueryStore";
import {useMobileViewStore} from "@/store/MobileViewStore";

const {apiHost, customerSymbol, searchId, apiKey} = config

type FilterValue = {
  id?: string | number,
  label?: string,
  aggregations?: FilterValue[],
  [key: string]: unknown
}

function normalizeSortLabel(label: unknown): string {
  if (label === null || label === undefined) {
    return ''
  }

  if (typeof label === 'string') {
    return label.trim()
  }

  if (typeof label === 'number') {
    return label.toString()
  }

  return String(label).trim()
}

function compareLabels(labelA: unknown, labelB: unknown): number {
  const normalizedA = normalizeSortLabel(labelA)
  const normalizedB = normalizeSortLabel(labelB)

  if (normalizedA === normalizedB) {
    return 0
  }

  try {
    return normalizedA.localeCompare(normalizedB, 'pl-PL', {sensitivity: 'base'})
  } catch (error) {
    return normalizedA.localeCompare(normalizedB)
  }
}

function shouldSortAggregationByType(aggregation: AggregationInterface): boolean {
  const type = aggregation?.type
  return type === 'checkbox' || type === 'box'
}

function isSizeAggregation(aggregation: AggregationInterface): boolean {
  const field = aggregation?.field

  if (field) {
    const normalizedField = String(field).toLowerCase()

    if (normalizedField === 'size' || normalizedField === 'rozmiar') {
      return true
    }
  }

  const label = aggregation?.label

  if (typeof label === 'string') {
    const normalizedLabel = label.trim().toLowerCase()

    if (normalizedLabel === 'rozmiar' || normalizedLabel === 'rozmiary') {
      return true
    }
  }

  return false
}

const CUSTOM_SIZE_TEXT_ORDER = [
  'XXXS', 'XXS', 'XS', 'XS/S', 'S', 'S/M', 'M', 'M/L',
  'L', 'L/XL', 'XL', 'XXL', 'XXXL', '3XL', '4XL', '5XL'
]

function computeCustomSizeWeight(label: unknown): number | null {
  if (label === null || label === undefined) {
    return null
  }

  const text = String(label).trim()

  if (!text) {
    return null
  }

  const upper = text.toUpperCase()

  if (upper === 'UNIWERSALNY' || upper === 'UNI') {
    return 999999
  }

  const numericPart = parseInt(upper, 10)
  const textOrderIndex = CUSTOM_SIZE_TEXT_ORDER.findIndex((token) => upper.includes(token))
  const hasTextOrder = textOrderIndex !== -1

  if (!Number.isNaN(numericPart)) {
    const baseWeight = numericPart * 10
    const decimalWeight = hasTextOrder ? textOrderIndex / 100 : 0
    return baseWeight + decimalWeight
  }

  if (hasTextOrder) {
    return 1000 + textOrderIndex
  }

  return null
}

type SizeSortType = 'text' | 'numeric' | 'range' | 'fraction'

type ParsedSizeLabel = {
  raw: string,
  type: SizeSortType,
  normalizedText?: string,
  fallbackText: string,
  numericValue?: number,
}

function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100
}

function sanitizeSizeSuffix(value: string): string {
  let sanitized = value
  const suffixPattern = /(?:-|\s)?(?:T|PETITE|LONG|REG)$/i

  while (suffixPattern.test(sanitized)) {
    sanitized = sanitized.replace(suffixPattern, '')
  }

  return sanitized
}

function parseSizeLabel(label: unknown): ParsedSizeLabel | null {
  if (typeof label !== 'string') {
    if (typeof label === 'number') {
      return {
        raw: String(label),
        type: 'numeric',
        fallbackText: String(label),
        numericValue: Number(label),
      }
    }

    return null
  }

  const trimmed = label.trim()

  if (!trimmed) {
    return null
  }

  const sanitized = trimmed.replace(/,/g, '.').replace(/\s+/g, ' ').trim()
  const fallbackText = sanitized.toUpperCase()

  const fractionMatch = sanitized.match(/^(\d+(?:\.\d+)?)\s+(\d+)\/(\d+)$/)

  if (fractionMatch) {
    const base = parseFloat(fractionMatch[1])
    const numerator = parseFloat(fractionMatch[2])
    const denominator = parseFloat(fractionMatch[3])

    if (!Number.isNaN(base) && !Number.isNaN(numerator) && !Number.isNaN(denominator) && denominator !== 0) {
      const numericValue = roundToTwoDecimals(base + numerator / denominator)

      return {
        raw: label,
        type: 'fraction',
        fallbackText,
        numericValue,
      }
    }
  }

  const slashMatch = sanitized.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/)

  if (slashMatch) {
    const first = parseFloat(slashMatch[1])
    const second = parseFloat(slashMatch[2])

    if (!Number.isNaN(first) && !Number.isNaN(second)) {
      const numericValue = roundToTwoDecimals((first + second) / 2)

      return {
        raw: label,
        type: 'range',
        fallbackText,
        numericValue,
      }
    }
  }

  const dashMatch = sanitized.match(/^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/)

  if (dashMatch) {
    const first = parseFloat(dashMatch[1])

    if (!Number.isNaN(first)) {
      return {
        raw: label,
        type: 'range',
        fallbackText,
        numericValue: roundToTwoDecimals(first),
      }
    }
  }

  const numericCandidate = sanitized.replace(/\s+/g, '')

  if (/^\d+(?:\.\d+)?$/.test(numericCandidate)) {
    const numericValue = parseFloat(numericCandidate)

    if (!Number.isNaN(numericValue)) {
      return {
        raw: label,
        type: 'numeric',
        fallbackText,
        numericValue,
      }
    }
  }

  const normalizedWithoutSpaces = sanitized.replace(/\s+/g, '')
  const normalizedText = sanitizeSizeSuffix(normalizedWithoutSpaces).toUpperCase()

  if (!normalizedText) {
    return {
      raw: label,
      type: 'text',
      fallbackText,
    }
  }

  return {
    raw: label,
    type: 'text',
    normalizedText,
    fallbackText,
  }
}

type SizeEntry = {
  value: FilterValue,
  index: number,
  parsed: ParsedSizeLabel | null,
  customWeight: number | null,
}

function compareByCustomWeight(entryA: SizeEntry, entryB: SizeEntry): number | null {
  const weightA = entryA.customWeight
  const weightB = entryB.customWeight
  const hasA = weightA !== null && weightA !== undefined && !Number.isNaN(weightA)
  const hasB = weightB !== null && weightB !== undefined && !Number.isNaN(weightB)

  if (!hasA && !hasB) {
    return null
  }

  if (!hasA) {
    return 1
  }

  if (!hasB) {
    return -1
  }

  if (weightA !== weightB) {
    return weightA - weightB
  }

  return entryA.index - entryB.index
}

function buildTextSortIndex(normalizedLabels: string[]): Map<string, number> {
  const uniqueLabels = Array.from(new Set(normalizedLabels.filter((label) => label.length > 0)))

  if (uniqueLabels.length === 0) {
    return new Map<string, number>()
  }

  let apparealSorted: string[] = []

  try {
    apparealSorted = sortApparelSizes(uniqueLabels.slice())
  } catch (error) {
    apparealSorted = []
  }

  if (!Array.isArray(apparealSorted)) {
    apparealSorted = []
  }

  const recognized = apparealSorted.filter((label) => uniqueLabels.includes(label))
  const missing = uniqueLabels
    .filter((label) => !recognized.includes(label))
    .sort((labelA, labelB) => labelA.localeCompare(labelB, undefined, {numeric: true, sensitivity: 'base'}))

  const finalOrder = recognized.concat(missing)
  const indexMap = new Map<string, number>()

  finalOrder.forEach((label, position) => {
    if (!indexMap.has(label)) {
      indexMap.set(label, position)
    }
  })

  return indexMap
}

function sortSizeFilterValues(values: FilterValue[]): FilterValue[] {
  const entries: SizeEntry[] = values.map((value, index) => ({
    value,
    index,
    parsed: parseSizeLabel(value?.label),
    customWeight: computeCustomSizeWeight(value?.label),
  }))

  const textEntries: SizeEntry[] = []
  const numericEntries: SizeEntry[] = []
  const rangeEntries: SizeEntry[] = []
  const fractionEntries: SizeEntry[] = []
  const unknownEntries: SizeEntry[] = []

  entries.forEach((entry) => {
    const parsed = entry.parsed

    if (!parsed) {
      unknownEntries.push(entry)
      return
    }

    switch (parsed.type) {
    case 'text':
      if (parsed.normalizedText) {
        textEntries.push(entry)
      } else {
        unknownEntries.push(entry)
      }
      break
    case 'numeric':
      numericEntries.push(entry)
      break
    case 'range':
      rangeEntries.push(entry)
      break
    case 'fraction':
      fractionEntries.push(entry)
      break
    default:
      unknownEntries.push(entry)
    }
  })

  if (textEntries.length > 0) {
    const textIndexMap = buildTextSortIndex(textEntries.map((entry) => entry.parsed?.normalizedText ?? ''))

    textEntries.sort((entryA, entryB) => {
      const weightComparison = compareByCustomWeight(entryA, entryB)
      if (weightComparison !== null && weightComparison !== 0) {
        return weightComparison
      }

      const normalizedA = entryA.parsed?.normalizedText ?? ''
      const normalizedB = entryB.parsed?.normalizedText ?? ''

      const indexA = textIndexMap.has(normalizedA) ? textIndexMap.get(normalizedA) as number : Number.POSITIVE_INFINITY
      const indexB = textIndexMap.has(normalizedB) ? textIndexMap.get(normalizedB) as number : Number.POSITIVE_INFINITY

      if (indexA !== indexB) {
        return indexA - indexB
      }

      const fallbackA = entryA.parsed?.fallbackText ?? normalizedA
      const fallbackB = entryB.parsed?.fallbackText ?? normalizedB

      const comparison = fallbackA.localeCompare(fallbackB, undefined, {numeric: true, sensitivity: 'base'})

      if (comparison !== 0) {
        return comparison
      }

      return entryA.index - entryB.index
    })
  }

  const sortByNumericValue = (list: SizeEntry[]): SizeEntry[] => list.sort((entryA, entryB) => {
    const weightComparison = compareByCustomWeight(entryA, entryB)
    if (weightComparison !== null && weightComparison !== 0) {
      return weightComparison
    }

    const valueA = entryA.parsed?.numericValue ?? Number.POSITIVE_INFINITY
    const valueB = entryB.parsed?.numericValue ?? Number.POSITIVE_INFINITY

    if (valueA !== valueB) {
      return valueA - valueB
    }

    const fallbackComparison = compareLabels(entryA.value?.label, entryB.value?.label)

    if (fallbackComparison !== 0) {
      return fallbackComparison
    }

    return entryA.index - entryB.index
  })

  sortByNumericValue(numericEntries)
  sortByNumericValue(rangeEntries)
  sortByNumericValue(fractionEntries)
  unknownEntries.sort((entryA, entryB) => {
    const weightComparison = compareByCustomWeight(entryA, entryB)

    if (weightComparison !== null && weightComparison !== 0) {
      return weightComparison
    }

    return entryA.index - entryB.index
  })

  return [
    ...textEntries,
    ...numericEntries,
    ...rangeEntries,
    ...fractionEntries,
    ...unknownEntries,
  ].map((entry) => entry.value)
}

function sortAggregationValues(values: FilterValue[] = [], aggregation: AggregationInterface): FilterValue[] {
  if (!Array.isArray(values) || values.length === 0) {
    return Array.isArray(values) ? values.slice() : []
  }

  const valuesWithSortedChildren = values.map((value) => {
    const clonedValue = {
      ...value,
    } as FilterValue

    if (Array.isArray(value?.aggregations) && value.aggregations.length > 0) {
      clonedValue.aggregations = sortAggregationValues(value.aggregations, aggregation)
    }

    return clonedValue
  })

  if (!shouldSortAggregationByType(aggregation)) {
    return valuesWithSortedChildren
  }

  if (isSizeAggregation(aggregation)) {
    return sortSizeFilterValues(valuesWithSortedChildren)
  }

  return valuesWithSortedChildren.sort((valueA, valueB) => compareLabels(valueA?.label, valueB?.label))
}

function sortAggregation(aggregation: AggregationInterface): AggregationInterface {
  if (!aggregation?.data || !Array.isArray(aggregation.data?.values)) {
    return aggregation
  }

  return {
    ...aggregation,
    data: {
      ...aggregation.data,
      values: sortAggregationValues(aggregation.data.values as FilterValue[], aggregation),
    },
  }
}

function cloneAggregationValues(values: FilterValue[] = []): FilterValue[] {
  return values.map((value) => {
    const clonedValue = {
      ...value,
    } as FilterValue

    if (value.aggregations) {
      clonedValue.aggregations = cloneAggregationValues(value.aggregations)
    }

    return clonedValue
  })
}

function getValueKey(value: FilterValue): string {
  if (value.id !== undefined && value.id !== null) {
    return String(value.id)
  }

  if (value.label !== undefined) {
    return `label:${String(value.label)}`
  }

  return JSON.stringify(value)
}

function mergeAggregationValues(existingValues: FilterValue[] = [], newValues: FilterValue[] = []): FilterValue[] {
  const mergedValues = cloneAggregationValues(existingValues)
  const valuesIndex = new Map<string, number>()

  mergedValues.forEach((value, index) => {
    valuesIndex.set(getValueKey(value), index)
  })

  newValues.forEach((value) => {
    const valueKey = getValueKey(value)
    const clonedValue = {
      ...value,
      ...(value.aggregations ? {aggregations: cloneAggregationValues(value.aggregations)} : {}),
    }

    if (valuesIndex.has(valueKey)) {
      const existingIndex = valuesIndex.get(valueKey)
      const existingValue = mergedValues[existingIndex]
      const mergedValue = {
        ...existingValue,
        ...clonedValue,
      }

      if (existingValue?.aggregations || clonedValue?.aggregations) {
        mergedValue.aggregations = mergeAggregationValues(existingValue?.aggregations ?? [], clonedValue?.aggregations ?? [])
      }

      mergedValues[existingIndex] = mergedValue
      return
    }

    valuesIndex.set(valueKey, mergedValues.length)
    mergedValues.push(clonedValue)
  })

  return mergedValues
}

function mergeAggregationData(existingData = {}, newData = {}) {
  const mergedData = {
    ...existingData,
    ...newData,
  }

  const existingValues = existingData?.values as FilterValue[] | undefined
  const newValues = newData?.values as FilterValue[] | undefined

  if (existingValues || newValues) {
    mergedData.values = mergeAggregationValues(existingValues ?? [], newValues ?? [])
  }

  return mergedData
}

function cloneAggregation(aggregation: AggregationInterface): AggregationInterface {
  return {
    ...aggregation,
    data: aggregation.data ? {
      ...aggregation.data,
      ...(aggregation.data?.values ? {values: cloneAggregationValues(aggregation.data.values as FilterValue[])} : {}),
    } : aggregation.data,
  }
}

function mergeAggregations(
    existingAggregations: AggregationInterface[] = [],
    newAggregations: AggregationInterface[] = [],
    {fieldsToMerge}: {fieldsToMerge?: Set<string>} = {}
): AggregationInterface[] {
  const shouldMergeAll = !fieldsToMerge || fieldsToMerge.size === 0
  const existingAggregationsMap = new Map<string, AggregationInterface>()

  existingAggregations.forEach((aggregation) => {
    existingAggregationsMap.set(aggregation.field, cloneAggregation(aggregation))
  })

  const processedAggregations: AggregationInterface[] = []
  const processedFields = new Set<string>()

  newAggregations.forEach((aggregation) => {
    const clonedAggregation = cloneAggregation(aggregation)
    const existingAggregation = existingAggregationsMap.get(clonedAggregation.field)
    const shouldMergeField = shouldMergeAll || fieldsToMerge?.has(clonedAggregation.field)

    processedFields.add(clonedAggregation.field)

    if (!shouldMergeField || !existingAggregation) {
      processedAggregations.push(sortAggregation(clonedAggregation))
      return
    }

    processedAggregations.push(sortAggregation({
      ...existingAggregation,
      ...clonedAggregation,
      data: mergeAggregationData(existingAggregation?.data ?? {}, clonedAggregation?.data ?? {}),
    }))
  })

  if (shouldMergeAll) {
    existingAggregations.forEach((aggregation) => {
      if (!processedFields.has(aggregation.field)) {
        processedAggregations.push(sortAggregation(cloneAggregation(aggregation)))
      }
    })

    return processedAggregations
  }

  fieldsToMerge?.forEach((field) => {
    if (processedFields.has(field)) {
      return
    }

    const existingAggregation = existingAggregationsMap.get(field)

    if (existingAggregation) {
      processedAggregations.push(sortAggregation(existingAggregation))
    }
  })

  return processedAggregations
}

function hasActiveFilterValue(value: unknown): boolean {
  if (!value) {
    return false
  }

  if (Array.isArray(value)) {
    return value.length > 0
  }

  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length > 0
  }

  return Boolean(value)
}


export const useFiltersStore = defineStore('FiltersStore', {
  state: () => {
    return {
      aggregations: [] as AggregationInterface[],
      activeFilters: {},
      isLoading: false,
      canShowFiltersOnMobile: false,
      activeRangeFilters: {},
      rangeFilters: {},
      lastSearchQuery: null as string | null
    }
  },
  getters: {
    getFilters(state): Array<AggregationInterface> {
      return state.aggregations.filter((item) => {
        const valuesLength = Array.isArray(item?.data?.values) ? item.data.values.length : 0
        const hasRangeValues = item?.data?.min != null || item?.data?.max != null
        return valuesLength > 0 || hasRangeValues
      })
    },
    getFiltersToTagList() {
      const {limitPerFilter} = SmartSearchConfig.value.serp.tagList;
      const activeFilters = this.getActiveFilters;
      
      return this.getCheckboxFilters.flatMap(filter =>
        filter.data.values
          .filter(value =>
            // Sprawdź, czy dana wartość nie znajduje się w aktywnych filtrach
            !(activeFilters[filter.field] && activeFilters[filter.field].includes(value.label))
          )
          .slice(0, limitPerFilter)
          .map(value => ({
            field: filter.field,
            label: filter.label,
            value
          }))
      );
    },
    
    getCheckboxFilters(state): Array<AggregationInterface> {
      return state.aggregations.filter((item) => {
        return item.type === 'checkbox'
      })
    },
    hasAnyFilters(state): number {
      return state.aggregations.length > 0
    },
    getFiltersLength(state): number {
      return state.aggregations.length
    },
    getActiveFilters(state) {
      return state.activeFilters
    },
    hasSelectedFilters(state) {
      return Object.keys(state.activeFilters).length > 0
    },
    getIsLoading(state) {
      return state.isLoading
    },
    getRangeInputs(state) {
      return state.rangeInputs
    },
    
    
  },
  actions: {
    setBodyScroll(allow: boolean) {
      if (allow) {
        document.body.classList.remove('no-scroll');
      } else {
        document.body.classList.add('no-scroll');
      }
    },
    showFiltersOnMobile() {
      this.canShowFiltersOnMobile = true
    },
    hideFiltersOnMobile() {
      this.canShowFiltersOnMobile = false
    },
    getFilterUnit(fieldName: string) {
      const aggregation = this.aggregations.find(aggregate => aggregate.field === fieldName)
      return aggregation?.data?.unit ?? ''
    },
    getFilterLabelByFilterName(fieldName: string): AggregationInterface {
      return this.aggregations.find(aggregate => aggregate.field === fieldName)?.label;
    },
    filterIsChecked(field: string, label: string) {
      const filterValue = this.getActiveFilters[field] as Array<string>;
      return filterValue?.includes(label) ?? false;
    },
    setActiveFilters(activeFilters): void {
      this.activeFilters = activeFilters
    },
    async updateCategoryItem(field: string, label: string) {
      await this.updateFilter(field, label)
    },
    updateRegisteredFilter(filterName: string, filterType: 'minValue' | 'maxValue', newValue: number | null) {
      if (this.rangeFilters[filterName]) {
        
        this.rangeFilters[filterName][filterType] = newValue
      }
      searchLogger.log(`updateRegisteredFilter ${filterName}.${filterType}`, newValue)
    },
    registerFilter(filterName: string, filterData: FilterRangeInterface) {
      this.rangeFilters[filterName] = filterData
      searchLogger.log(`registerFilter ${filterName}`, filterData)
    },
    
    registerAsActiveFilter(filterName: string, filterData: FilterRangeInterface) {
      this.activeRangeFilters[filterName] = filterData
      searchLogger.log(`registerAsActiveFilter ${filterName}`, filterData)
    },
    
    async updateRangeValue(e: Event, field: string, filterData: FilterRangeInterface, minValue, maxValue) {
      if (minValue < 0 || maxValue < 0) {
        return
      }
      
      await this.updateRangeFilter(field, minValue, maxValue);
    },
    
    async updateRangeFilter(field: string, min: number, max: number) {
      if ((!min && !max) || (min && max && min > max) || (min && max && max < min)) {
        return
      }
      
      const paginationStore = usePaginationStore();
      const urlStore = useUrlStore()
      const url = urlStore.decomposeUrl();
      
      const rangeData = {}
      if (min) {
        rangeData.min = Number(min)
      }
      if (max) {
        rangeData.max = Number(max)
      }
      
      urlStore.addRangeFiltersToParameters(url, field, rangeData)
      this.activeFilters[field] = rangeData;
      
      paginationStore.resetPageValue(url)
      urlStore.updateUrl(url);
      await this.fetchFiltersAnProducts()
      
    },
    
    clearInputValues(filterName: string): void {
      this.updateRegisteredFilter(filterName, 'minValue', this.rangeFilters[filterName]?.min ?? null)
      this.updateRegisteredFilter(filterName, 'maxValue', this.rangeFilters[filterName]?.max ?? null)
    },
    async removeRangeFilter(filterName: string) {
      
      const urlStore = useUrlStore()
      const paginationStore = usePaginationStore()
      const serpResultsStore = useSerpResultsStore()
      const url = urlStore.decomposeUrl();
      urlStore.removeRangeFilterFromParameters(url, filterName)
      
      paginationStore.resetPageValue(url)
      urlStore.updateUrl(url);
      
      await this.fetchFiltersAnProducts()
      serpResultsStore.scrollIntoView()
    },
    
    
    async updateFilter(field: string, label: string) {

      const paginationStore = usePaginationStore();
      const urlStore = useUrlStore()
      const serpResultsStore = useSerpResultsStore()
      const mobileViewStore = useMobileViewStore()
      const filterValue = [...(this.activeFilters[field] as Array<string> || [])];
      const filterIsChecked = this.filterIsChecked(field, label)

      const url = urlStore.decomposeUrl();

      if (filterIsChecked) {
        filterValue.splice(filterValue.indexOf(label), 1);
      } else {
        filterValue.push(label);
      }
      
      urlStore.updateFiltersParameters(url, field, filterValue)
      
      this.activeFilters[field] = filterValue;
      paginationStore.resetPageValue(url)

      urlStore.updateUrl(url);
      await this.updateFilters()

      if (!mobileViewStore.isMobileView) {
        serpResultsStore.scrollIntoView()
      }


    },
    async clearFilterByField(field: string) {
      const currentFilter = this.activeFilters[field]

      if (!currentFilter) {
        return
      }

      if (typeof currentFilter === 'object' && !Array.isArray(currentFilter)) {
        await this.removeRangeFilter(field)
        return
      }

      const urlStore = useUrlStore()
      const paginationStore = usePaginationStore()
      const serpResultsStore = useSerpResultsStore()
      const mobileViewStore = useMobileViewStore()
      const url = urlStore.decomposeUrl();

      const restFilters = {...this.activeFilters}
      delete restFilters[field]
      this.activeFilters = restFilters

      urlStore.removeFilterFromParameters(url, field)

      paginationStore.resetPageValue(url)
      urlStore.updateUrl(url)

      await this.fetchFiltersAnProducts()

      if (!mobileViewStore.isMobileView) {
        serpResultsStore.scrollIntoView()
      }
    },
    async updateFilters() {
      await this.fetchFiltersAnProducts()
    },
    async fetchAll(){
      const serpResultsStore = useSerpResultsStore()
      await Promise.all([
        serpResultsStore.getAndSetProducts('changeFilter'),
        this.fetchFilters()
      ])
    },
    
    async fetchFiltersAnProducts() {
      if(window?._QONizer?.customSmartSearchConfig?.skipFetchFilters){
        searchLogger.log('! skipping Fetch Filters to allow multiple filters')
       
        const serpResultsStore = useSerpResultsStore()
        await Promise.all([
          serpResultsStore.getAndSetProducts('changeFilter'),
        ])
        return
      }
      searchLogger.log('Fetching with filters')
      await this.fetchAll()
      
    },
    clearFilterValues() {
      this.activeRangeFilters = {}
    },
    
    async clearSelectedFilters() {
      const serpResultsStore = useSerpResultsStore()
      const mobileViewStore = useMobileViewStore()
      const urlStore = useUrlStore()
      this.activeFilters = {};
      
      urlStore.removeFiltersFromUrl()
      // this.clearFilterValues()
      // this.hideFiltersOnMobile()
      await this.fetchFiltersAnProducts()
      if (!mobileViewStore.isMobileView) {
        serpResultsStore.scrollIntoView()
      }
    },
    
    getSearchQueryParams(url: URL): URL {
      const urlParams = useSearchQueryStore().getSearchParams()
      const searchQuery = useSearchQueryStore().getSearchQuery
      
      const sortingStore = useSortingStore();
      const {selectedSort} = storeToRefs(sortingStore)
      const paginationStore = usePaginationStore()
      const {page} = storeToRefs(paginationStore)
      page.value !== 0 && url.searchParams.set('p', page.value)
      urlParams.forEach((value, key) => {
        if (key.match(/^f/)) {
          url.searchParams.set(key, value);
        }
      });
      
      searchQuery && url.searchParams.set('q', searchQuery)
      url.searchParams.set('s', selectedSort.value)
      return url
    },
    
    async fetchFilters(source = 'unknown'): Promise<void> {
      const searchQuery = useSearchQueryStore().getSearchQuery
      searchLogger.log('fetchFilters SearchQuery ', searchQuery)
      
      const {allowEmptyQuery} = SmartSearchConfig.value.serp.query
      
      if (!allowEmptyQuery && !searchQuery) {
        searchLogger.log('Not allowed to show filters on empty query')
        return
      }
      let url = new URL(`https://${apiHost}/restapi/${customerSymbol}/search/${searchId}/aggregations`)
      url = this.getSearchQueryParams(url)
      searchLogger.log(`fetchFilters url ${source}: ${url.toString()}`)
      
      
      
      const config = {
        headers: {
          'Api-key': apiKey
        },
        credentials: 'include'
      }
      
      try {
        this.isLoading = true
        const response = await fetch(
          url.toString(),
          config
        );
        
        if (!response.ok) {
          this.isLoading = false
          throw new Error('Network response was not ok');
          
        }
        
        const {aggregations} = await response.json();

        const searchQuery = useSearchQueryStore().getSearchQuery
        const isNewQuery = this.lastSearchQuery !== searchQuery

        const fieldsToMerge = Object.entries(this.activeFilters)
            .filter(([, value]) => hasActiveFilterValue(value))
            .map(([field]) => field)

        this.aggregations = isNewQuery
          ? mergeAggregations([], aggregations)
          : mergeAggregations(
              this.aggregations,
              aggregations,
              fieldsToMerge.length ? {fieldsToMerge: new Set(fieldsToMerge)} : {}
            )

        this.lastSearchQuery = searchQuery
        this.isLoading = false

      } catch (error) {
        this.isLoading = false
        searchErrorLogger.log('Error fetching filters:', error);
      }
    }
  },
})
